import type { ExerciseTypeConfig } from '../registry/types';
import type { ConversationalExerciseDefinition, ConversationalExerciseSubmission } from '../types';
import { conversationalSubmissionSchema } from '../types/schemas';

/**
 * Get the normalized max score from a definition
 */
function getNormalizedMaxScore(definition: ConversationalExerciseDefinition, fallback: number): number {
  const maxScore = definition.maxScore;
  if (typeof maxScore === 'number' && Number.isFinite(maxScore) && maxScore > 0) {
    return maxScore;
  }
  return fallback;
}

/**
 * Conversational exercise type configuration
 */
export const conversationalConfig: ExerciseTypeConfig<
  'Conversational',
  ConversationalExerciseDefinition,
  ConversationalExerciseSubmission,
  { kind: string }
> = {
  type: 'Conversational',
  isAutoGradable: false,
  submissionSchema: conversationalSubmissionSchema,

  defaultMaxScore: (definition) => {
    return getNormalizedMaxScore(definition, 100);
  },

  hydrateDetails: (serverDetails: unknown) => {
    const details = serverDetails as Record<string, unknown> | null ?? {};
    
    // Parse targetSkills from JSON string or array
    let targetSkills: string[] = [];
    if (Array.isArray(details.targetSkills)) {
      targetSkills = details.targetSkills;
    } else if (typeof details.targetSkills === 'string') {
      try {
        targetSkills = JSON.parse(details.targetSkills);
      } catch {
        targetSkills = [];
      }
    }

    return {
      theme: details.theme ?? '',
      maxDurationSec: details.maxDurationSec ?? 300,
      systemPrompt: details.systemPrompt ?? '',
      targetSkills,
    };
  },

  createDefault: (order) => ({
    type: 'Conversational' as const,
    theme: '',
    maxDurationSec: 300,
    systemPrompt: null,
    targetSkills: [],
  }),
};
